import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Award } from "lucide-react";

import { referralApi } from "@/lib/extensions-api";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { CopyableId } from "@/components/ui/CopyableId";

export function ReferralCommissionsPage() {
  const [userFilter, setUserFilter] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-referral-commissions", userFilter],
    queryFn: () => referralApi.admin.transactions(
      userFilter ? { user_id: userFilter } : {}
    ),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Referral Commission Transactions</h1>
        <p className="text-sm text-surface-500 mt-1">
          Immutable history of every commission processed by the Referral Engine.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>All Commissions</CardTitle>
          <Input
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value.toUpperCase())}
            placeholder="Filter by Sponsor User ID"
            className="w-64"
          />
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (data ?? []).length === 0 ? (
            <EmptyState icon={Award} title="No commissions found" />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Source Tx</TH>
                  <TH>Source User</TH>
                  <TH>Sponsor</TH>
                  <TH className="text-right">Source Amt</TH>
                  <TH className="text-right">Rate</TH>
                  <TH className="text-right">Commission</TH>
                  <TH>Status</TH>
                  <TH>When</TH>
                </TR>
              </THead>
              <tbody>
                {data!.map((c) => (
                  <TR key={c.id}>
                    <TD className="font-mono text-xs">{c.source_wallet_transaction_id.slice(0, 20)}…</TD>
                    <TD><CopyableId value={c.source_user_id} /></TD>
                    <TD><CopyableId value={c.sponsor_user_id} /></TD>
                    <TD className="text-right">{formatCurrency(c.source_amount)}</TD>
                    <TD className="text-right text-xs">
                      {(Number(c.commission_rate) * 100).toFixed(2)}%
                    </TD>
                    <TD className="text-right font-semibold text-success-700">
                      {formatCurrency(c.commission_amount)}
                    </TD>
                    <TD><StatusPill status={c.status} /></TD>
                    <TD className="text-xs text-surface-500">{formatDateTime(c.created_at)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
